import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { useToast } from "@faro-lotv/flat-ui";
import { isInternalUserEmail } from "@faro-lotv/foreign-observers";
import { ApiResponseError } from "@faro-lotv/service-wires";
import { useCallback } from "react";

/**
 * Webhook entry-point defined by this Microsoft PowerAutomate flow: https://make.powerautomate.com/environments/3c7a3962-5bba-e1b7-b359-2f0f9e8b8b08/flows/6db99267-098b-55c7-a6e7-87a3184fdbf6/details
 *
 * See https://faro01.atlassian.net/wiki/spaces/SWEB/pages/5734432816/Feedback+collection for more information on how to get access to feedback or the PowerAutomate flow.
 */
const PRODUCT_FEEDBACK_WEBHOOK =
  "https://3c7a39625bbae1b7b3592f0f9e8b8b.08.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/74f797e72fde472093053dc21536ce33/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=HEd5SJ02fmMm23oXXg5L7ysVo8CzJlSQhsvIAIzaq4w";

/**
 * Features that we currently collect feedback for.
 * The values are shown together with the feedback, so they should be human-readable.
 * You can add new entries here when needed, the webhook will accept any string.
 */
export enum ProductFeedbackFeature {
  pointCloudAnalysis = "Point cloud analysis",
  floorPlanGeneration = "Floor plan generation",
}

/** The parameters accepted by the product feedback endpoint. */
type ProductFeedbackParams = {
  /** A broad grouping by feature. */
  feature: ProductFeedbackFeature;

  /** The question asked to the user. */
  question: string;

  /** The rating given by the user (1-5 - where 5 is the best). */
  rating: number;

  /** The optional, user-provided answer to the feedback question. */
  message?: string;

  /**
   * Additional technical information for the feedback. Use for not directly user-provided information that helps to convey the context,
   * e.g. an order ID to which this feedback was given for.
   */
  additionalInfo?: string;

  /** The email of the user giving feedback. */
  email?: string;

  /** Whether the feedback is from an internal user. */
  isInternal: boolean;

  /** Whether the user has given permission to be contacted about the feedback. */
  hasUserAllowedContact?: boolean;

  /** A link to the current project to provide better context for support requests. */
  projectLink: string;
};

/** The ProductFeedbackParams excluding all properties already auto-filled by the useSubmitProductFeedback hook. */
type UseSubmitProductFeedbackProps = Omit<
  ProductFeedbackParams,
  "projectLink" | "email" | "isInternal"
>;

/** Callback to submit product feedback. Returns whether the submission was successful. */
type UseSubmitProductFeedbackCallback = (
  feedbackContent: UseSubmitProductFeedbackProps,
) => Promise<boolean>;

/**
 * @returns a callback function to submit product feedback, showing toasts when the operation succeeds or fails.
 */
export function useSubmitProductFeedback(): UseSubmitProductFeedbackCallback {
  const { handleErrorWithToast } = useErrorHandlers();
  const { openToast } = useToast();

  const email = useAppSelector(selectCurrentUser)?.email;

  return useCallback(
    async (feedbackContent: UseSubmitProductFeedbackProps) => {
      try {
        await submitProductFeedback({
          projectLink: window.location.href,
          email,
          isInternal: isInternalUserEmail(email),
          ...feedbackContent,
        });

        openToast({
          title: "Your feedback has been submitted.",
        });

        return true;
      } catch (error) {
        handleErrorWithToast({
          title: "Could not submit feedback. Please try again later.",
          error,
        });

        return false;
      }
    },
    [email, handleErrorWithToast, openToast],
  );
}

/**
 * Submits a product feedback to our feedback collection mechanism.
 * Currently these will be logged to a teams channel and additionally to an Excel sheet.
 *
 * @param feedbackContent the content of the feedback to submit
 */
async function submitProductFeedback(
  feedbackContent: ProductFeedbackParams,
): Promise<void> {
  const response = await fetch(PRODUCT_FEEDBACK_WEBHOOK, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(feedbackContent),
  });

  if (!response.ok) {
    throw new ApiResponseError(
      response.status,
      response.statusText,
      response.body,
    );
  }
}
